import { Link } from "react-router-dom";
import { formatCr, formatDate, GATES, GATE_LABELS, TEMPERATURE_COLORS } from "@/lib/utils-ros";
import { Flame, PauseCircle, Loader2, ChevronDown } from "lucide-react";

/**
 * Vertical funnel — 5 stacked gate segments with opp cards, plus a parking lot.
 * Shared between the standalone Funnel page and the Opportunities "Funnel" view toggle,
 * so both stay in sync instead of drifting apart.
 */
export function FunnelBoard({ opps, canMoveDeals, moving, onMove, onPark, onParkRequest, fromPath = "/funnel", fromLabel = "Funnel" }) {
  const active = opps.filter(o => o.status === "active");
  const parked = opps.filter(o => ["parked", "on_hold", "lost"].includes(o.status));
  const byGate = GATES.reduce((acc, g) => { acc[g] = active.filter(o => o.current_gate === g); return acc; }, {});

  // Funnel narrowing widths per gate (as % of max)
  const WIDTHS = [100, 88, 76, 64, 52];

  return (
    <div data-testid="funnel-board">
      <div
        className="-mx-4 px-4 overflow-x-auto overscroll-x-contain md:mx-0 md:px-0 md:overflow-visible"
        data-testid="funnel-gates-scroll"
      >
        <div className="min-w-[760px] md:min-w-0 pt-1" data-testid="funnel-vertical">
          {GATES.map((g, i) => {
            const items = byGate[g];
            const val = items.reduce((s, o) => s + (o.value_cr || 0), 0);
            const width = WIDTHS[i];
            const isLast = i === GATES.length - 1;
            return (
              <div key={g} className="relative">
                <div className="mx-auto transition-all" style={{ maxWidth: `${width}%` }} data-testid={`funnel-gate-${g}`}>
                  <div className="relative border border-[#B9D6E5] rounded-xl overflow-hidden bg-white shadow-[0_4px_14px_rgba(7,83,154,0.05)]">
                    <div className="flex items-center justify-between px-4 md:px-5 py-1.5 bg-[#07539A] border-b border-[#064783]">
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="w-6 h-6 rounded-md bg-[#E58D22] text-[#FFFFFF] text-[10px] font-semibold flex items-center justify-center">G{i + 1}</span>
                        <span className="text-[12px] md:text-[13px] font-semibold text-[#FFFFFF]">{GATE_LABELS[g]}</span>
                      </div>
                      <div className="flex items-center gap-3 md:gap-5 text-right">
                        <div>
                          <div className="text-[8px] uppercase tracking-widest leading-none text-[#CFE8F5]">Deals</div>
                          <div className="text-[12px] leading-tight font-semibold tabular-nums text-[#CFE8F5]">{items.length}</div>
                        </div>
                        <div>
                          <div className="text-[8px] uppercase tracking-widest leading-none text-[#CFE8F5]">Value</div>
                          <div className="text-[12px] leading-tight font-semibold tabular-nums text-[#CFE8F5]">{formatCr(val)}</div>
                        </div>
                      </div>
                    </div>

                    <div className="p-3 md:p-4 min-h-[82px] flex flex-wrap gap-2.5">
                      {items.length === 0 ? (
                        <div className="w-full text-center py-4 text-[12px] text-[#90A4AE]">No deals in this gate</div>
                      ) : items.map(o => (
                        <FunnelDealCard
                          key={o.id}
                          o={o}
                          onMoveNext={canMoveDeals && i < GATES.length - 1 ? () => onMove(o.id, GATES[i + 1]) : null}
                          onMovePrev={canMoveDeals && i > 0 ? () => onMove(o.id, GATES[i - 1]) : null}
                          onPark={canMoveDeals ? () => onParkRequest(o) : null}
                          moving={moving === o.id}
                          clickable={canMoveDeals}
                          fromPath={fromPath}
                          fromLabel={fromLabel}
                        />
                      ))}
                    </div>
                  </div>
                </div>

                {!isLast && (
                  <div className="flex justify-center py-1" aria-hidden="true">
                    <ChevronDown size={17} className="text-[#E58D22]" strokeWidth={2} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-5 bg-white border border-[#C8DCE7] rounded-2xl overflow-hidden shadow-[0_10px_28px_rgba(10,42,67,0.06)]" data-testid="parking-lot">
        <div className="p-4 md:px-5 border-b border-[#C8DCE7] bg-[#E6F5FB] flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-lg bg-white text-[#07539A] border border-[#C8DCE7] flex items-center justify-center"><PauseCircle size={15} /></span>
          <span className="font-display text-base text-[#12324A]">Parking Lot</span>
          <span className="ml-auto px-2.5 py-1 rounded-full bg-white text-[11px] font-semibold text-[#607D8B] border border-[#C8DCE7]">{parked.length}</span>
        </div>
        {parked.length === 0 ? (
          <div className="p-7 text-[13px] text-[#78909C] text-center">Nothing parked. All hands on deck.</div>
        ) : (
          <div className="divide-y divide-[#E2ECF1]">
            {parked.map(o => (
              <div key={o.id} className="p-4 md:px-5 flex items-center gap-3 hover:bg-[#F8FCFE] transition-colors">
                <div className="flex-1 min-w-0">
                  <Link to={`/opportunities/${o.id}`} state={{ from: fromPath, fromLabel }} className="text-[13px] font-medium text-[#12324A] hover:text-[#07539A] truncate block">{o.name}</Link>
                  <div className="text-[11px] text-[#78909C] mt-0.5">{formatCr(o.value_cr)} · Status: {o.status.replace("_", " ")}</div>
                  {o.status === "parked" && (o.park_revisit_date || o.park_reason) && (
                    <div className="text-[11px] text-[#526E7A] mt-1 truncate">
                      {o.park_revisit_date && <>Revisit {formatDate(o.park_revisit_date)}</>}
                      {o.park_revisit_date && o.park_reason && " · "}
                      {o.park_reason}
                    </div>
                  )}
                </div>
                {canMoveDeals && (
                <button
                  onClick={() => onPark(o.id, "active")}
                  disabled={moving === o.id}
                  data-testid={`unpark-${o.id}`}
                  className="px-3 py-1.5 rounded-lg border border-[#9FC8DB] bg-[#E6F5FB] text-[11px] font-semibold text-[#07539A] hover:bg-[#D6EEF8]"
                >
                  {moving === o.id ? "…" : "Reactivate"}
                </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FunnelDealCard({ o, onMoveNext, onMovePrev, onPark, moving, clickable = true, showValue = true, fromPath, fromLabel }) {
  const modules = getOpportunityModules(o);

  const body = (
    <>
      <div className="text-[12px] font-semibold text-[#12324A] leading-snug mb-1 truncate">{o.company?.name || "Company"}</div>
      <div className="text-[11px] text-[#78909C] truncate">{modules.length ? modules.join(" · ") : "No modules selected"}</div>
      <div className="flex items-center justify-between mt-1.5">
        {showValue
          ? <div className="text-[12px] tabular-nums font-semibold text-[#07539A]">{formatCr(o.value_cr)}</div>
          : <div />}
        <div className="flex items-center gap-1">
          <Flame size={11} style={{ color: TEMPERATURE_COLORS[o.temperature] }} />
          <span className="text-[10px] uppercase" style={{ color: TEMPERATURE_COLORS[o.temperature] }}>{o.temperature}</span>
        </div>
      </div>
    </>
  );

  return (
    <div className="bg-[#EDF3F6] border border-[#D0DEE5] rounded-xl shadow-[0_4px_12px_rgba(10,42,67,0.06)] hover:shadow-[0_8px_20px_rgba(7,83,154,0.11)] hover:border-[#87BDD5] transition-all flex flex-col min-w-[180px] max-w-[245px] flex-1 overflow-hidden" data-testid={`funnel-card-${o.id}`}>
      {clickable ? (
        <Link to={`/opportunities/${o.id}`} state={{ from: fromPath, fromLabel }} className="block p-3 flex-1">
          {body}
        </Link>
      ) : (
        <div className="p-3 flex-1">{body}</div>
      )}
      {(onMovePrev || onPark || onMoveNext) && (
        <div className="flex border-t border-[#CAD9E0] bg-[#DCE7EC]">
          {onMovePrev && (
            <button
              onClick={onMovePrev}
              disabled={moving}
              data-testid={`prev-${o.id}`}
              className="flex-1 text-[11px] py-1.5 text-[#526E7A] hover:text-[#07539A] hover:bg-[#CFDFE6] border-r border-[#CAD9E0]"
            >←</button>
          )}
          {onPark && (
            <button
              onClick={onPark}
              disabled={moving}
              data-testid={`park-${o.id}`}
              className="flex-1 text-[11px] py-1.5 text-[#526E7A] hover:text-[#C26A08] hover:bg-[#CFDFE6] border-r border-[#CAD9E0]"
            >
              {moving ? <Loader2 size={11} className="animate-spin mx-auto" /> : "Park"}
            </button>
          )}
          {onMoveNext && (
            <button
              onClick={onMoveNext}
              disabled={moving}
              data-testid={`next-${o.id}`}
              className="flex-1 text-[11px] py-1.5 text-[#D9790B] hover:bg-[#CFDFE6]"
            >→</button>
          )}
        </div>
      )}
    </div>
  );
}

function getOpportunityModules(opportunity) {
  const lineItemModules = (opportunity.line_items || [])
    .filter(item => !["lost", "cancelled"].includes(item.status))
    .map(item => item.ergoniq_module || item.label)
    .filter(Boolean);
  const source = lineItemModules.length ? lineItemModules : (opportunity.products || []);
  return [...new Set(source.map(value => String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, letter => letter.toUpperCase())
  ))];
}
